/**
 * Pure derivation helpers for the class-form "Add Teaching Subject"
 * dialog. Kept in a React-free module so unit tests can load them
 * without pulling in MUI or the RTK Query hooks the component uses.
 * Re-exported from AddTeachingSubjectDialog.tsx for callers.
 */
import type { IGrade, ISubjectGradePerent } from "../../../Api/Interface/api.interface";

export interface SubjectOption {
  subject_id: number;
  name: string;
  /** All grade_subject rows for this subject the staff is qualified to
   *  teach. Each entry keeps the pivot id + its grade. */
  pairs: Array<{ grade_subject_id: number; grade_id: number; grade_name: string }>;
}

/**
 * Given the same `subjectGRadeDataList` ClassForm renders, groups the
 * pairs by subject_id and only keeps the ones the assigned staff can
 * teach. Any subject with zero teachable pairs is dropped so the picker
 * never offers something the current teacher cannot cover.
 */
export const buildSubjectOptionsFromGrades = (
  subjectGRadeDataList: IGrade[],
  subjectMap: Map<number, string>,
): SubjectOption[] => {
  const bySubject = new Map<number, SubjectOption>();
  subjectGRadeDataList.forEach((grade) => {
    (grade.selected_sub_array || []).forEach((sub: ISubjectGradePerent) => {
      if (!(sub as any).is_assigned_to_staff) return;
      const subId = sub.subject_id;
      const gradeSubjectId = sub.id;
      if (!bySubject.has(subId)) {
        bySubject.set(subId, {
          subject_id: subId,
          name: subjectMap.get(subId) || `Subject #${subId}`,
          pairs: [],
        });
      }
      bySubject.get(subId)!.pairs.push({
        grade_subject_id: gradeSubjectId,
        grade_id: Number((grade as any).id),
        grade_name: String((grade as any).grade || `Grade #${(grade as any).id}`),
      });
    });
  });
  const list = Array.from(bySubject.values());
  list.forEach((o) => o.pairs.sort((a, b) => a.grade_name.localeCompare(b.grade_name)));
  list.sort((a, b) => a.name.localeCompare(b.name));
  return list;
};

/**
 * Given the subject's teachable pairs, the pair-ids currently on the
 * class, and the protected pair-ids, returns the ids to preselect when
 * the admin picks that subject. "Already on class" and "protected" pairs
 * are excluded so the picker never lets a duplicate through.
 */
export const resolvePreselectedGrades = (
  subject: SubjectOption | null,
  alreadyOnClass: ReadonlySet<number>,
  protectedIds?: ReadonlySet<number>,
): number[] => {
  if (!subject) return [];
  return subject.pairs
    .filter((p) => !alreadyOnClass.has(p.grade_subject_id))
    .filter((p) => !protectedIds || !protectedIds.has(p.grade_subject_id))
    .map((p) => p.grade_subject_id);
};
